import './styles.scss';
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { Dropdown, DropdownButton, Container } from '@edx/paragon';
import { getCourseRunTilePrimaryText } from '@utils/courseRunSelection';

import { defineMessages, injectIntl } from 'gatsby-plugin-react-intl';

import { intlShape } from '@prospectus/common/types/intlShape';
import { WebviewContext } from '@utils/context';
import courseRunShape from '@prospectus/common/types/courseRunShape';
import EnrollButtonLink from '../EnrollButton/EnrollButtonLink';

const messages = defineMessages({
  'prospectus.course.nav.about': {
    id: 'prospectus.course.nav.about',
    description: 'A link that brings the user to the about section.',
    defaultMessage: 'About',
  },
  'prospectus.course.nav.enroll': {
    id: 'prospectus.course.nav.enroll',
    description: 'Enroll button label',
    defaultMessage: 'Enroll now',
  },
  'prospectus.course.nav.outcome': {
    id: 'prospectus.course.nav.outcome',
    description: 'A link that brings the user to the outcomes section',
    defaultMessage: "What you'll learn",
  },
  'prospectus.course.nav.syllabus': {
    id: 'prospectus.course.nav.syllabus',
    description: 'A link that brings the user to the syllabus section',
    defaultMessage: 'Syllabus',
  },
  'prospectus.course.nav.curriculum': {
    id: 'prospectus.course.nav.curriculum',
    description: 'A link that brings the user to the course curriculum section.',
    defaultMessage: 'Curriculum',
  },
  'prospectus.course.nav.certificate': {
    id: 'prospectus.course.nav.certificate',
    description: 'A link that brings the user to the course certificate section.',
    defaultMessage: 'Certificate',
  },
  'prospectus.course.nav.instructors': {
    id: 'prospectus.course.nav.instructors',
    description: 'A link that brings the user to the instructors section.',
    defaultMessage: 'Instructors',
  },
  'prospectus.course.nav.faqs': {
    id: 'prospectus.course.nav.faqs',
    description: 'A link that brings the user to the frequently asked questions section.',
    defaultMessage: 'FAQs',
  },
  'prospectus.course.nav.modes': {
    id: 'prospectus.course.nav.modes',
    description: 'A link that brings the user to a comparison of paid versus free benefits for the course.',
    defaultMessage: 'Ways to enroll',
  },
});

function BaseStickyNav({
  faq,
  fullDescription,
  outcome,
  syllabusRaw,
  title,
  scrollToRef,
  semanticRefMap,
  showCourseModes,
  intl,
  uuid,
  emailOptIn,
  defaultCourseRun,
  owners,
  courseType,
  subjects,
}) {
  const [currentSection, setCurrentSection] = useState('nav-about');
  const [isNavStuck, setIsNavStuck] = useState(false);
  const navRef = useRef(null);
  const isSpanish = intl.locale === 'es';

  // This observer creates the additional nav-title-wrapper
  useEffect(() => {
    // Creating an Observer on mount to detect when navbar touches the top of the screen
    // and changes state appropriately
    // https://sung.codes/blog/2019/08/24/react-sticky-event-with-intersection-observer/
    const navObserver = new IntersectionObserver(
      // passed an array of entries, only need first
      ([entry]) => {
        const {
          intersectionRatio,
          intersectionRect: { y },
        } = entry;
        setIsNavStuck(intersectionRatio > 0 && y <= 0);
      },
      // multiple thresholds reduce the risk of a race condition that
      // makes us scroll past 100% visibility before we can set the stickiness
      { threshold: [0.98, 0.99, 1.0] },
    );

    navObserver.observe(navRef.current);

    return () => {
      if (navRef.current) {
        navObserver.unobserve(navRef.current);
      }
    };
  }, [navRef]);

  useEffect(() => {
    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        const refKey = Object.keys(semanticRefMap).find((key) => semanticRefMap[key].current === entry.target);
        if (refKey && refKey !== currentSection && entry.isIntersecting) {
          setCurrentSection(refKey);
        }
      });
    };

    const sectionObserver = new IntersectionObserver(handleIntersection, {
      rootMargin: '-25% 0px -75% 0px',
      threshold: [0, 1],
    });

    Object.values(semanticRefMap).forEach((ref) => {
      if (ref.current) {
        sectionObserver.observe(ref.current);
      }
    });

    return () => {
      Object.values(semanticRefMap).forEach((ref) => {
        if (ref.current) {
          sectionObserver.unobserve(ref.current);
        }
      });
    };
  }, [semanticRefMap, currentSection]);

  const handleClick = (event) => {
    event.preventDefault();
    scrollToRef(event);

    if (event.target.id === 'nav-about') {
      // because the about section is the first section beneath the sticky nav,
      // just scrolling to the section will not allow the nav to hit the top
      // of the page and therefore show the banner / enroll button.
      // so we're manually scrolling the page down by another few pixels.
      window.scrollBy(0, 15);
    }

    setCurrentSection(event.target.id);
  };

  const handleMobileNavClick = (event) => {
    event.preventDefault();
    scrollToRef(event);

    if (event.target.id === 'mobile-nav-about') {
      window.scrollBy(0, 30);
    }
    // slicing since section name is embedded in id
    setCurrentSection(event.target.id.slice(7));
  };

  const getListStyling = (id) => (id === currentSection ? `h4 list-${id}` : `list-${id}`);

  const getValidNavLinks = () => {
    const allNavLinks = ['about', 'outcome', 'syllabus', 'instructors', 'faqs', 'modes'];
    // remove if course section is not populated/missing
    return allNavLinks.filter(
      (navLink) => !(
        (navLink === 'about' && !fullDescription)
          || (navLink === 'outcome' && !outcome)
          || (navLink === 'syllabus' && !syllabusRaw)
          || (navLink === 'faqs' && !faq)
          // HACK: hide "Ways to Enroll" on the Spanish page until we sort out the design for the longer labels
          || (navLink === 'modes' && (!showCourseModes || isSpanish))
          // Do not show instructor button if there are no instructors for the course run
          || (navLink === 'instructors'
            && !(Object.keys(defaultCourseRun).length > 0 && defaultCourseRun.staff.length > 0))
      ),
    );
  };

  return (
    <WebviewContext.Consumer>
      {(webview) => (
        <section ref={navRef} className="page-nav sticky-top">
          {!webview && (
            <div className={`d-none nav-title-wrapper bg-primary-500 ${isNavStuck ? 'd-sm-block' : ''}`}>
              <h5 className="h5 text-primary-100 mx-auto py-2 mb-0">{title}</h5>
            </div>
          )}
          <div className="nav-enroll-wrapper bg-light-400">
            <Container
              size="lg"
              className="nav-enroll-container mx-auto d-flex align-items-center justify-content-between"
            >
              <div
                className={classNames(
                  'mobile-nav py-1',
                  { 'py-lg-0 d-lg-none': !isSpanish },
                  { 'py-xl-0 d-xl-none': isSpanish },
                )}
              >
                <DropdownButton
                  id="mobile-nav"
                  variant="custom"
                  // passing a span through for text-overflow styling via CSS
                  title={(
                    <span className="dropdown-title">
                      {intl.formatMessage(messages[`prospectus.course.nav.${currentSection.slice(4)}`])}
                    </span>
                  )}
                  className="my-2 bg-white"
                >
                  {getValidNavLinks().map((key) => (
                    <Dropdown.Item onClick={handleMobileNavClick} id={`mobile-nav-${key}`} key={`mobile-nav-${key}`}>
                      {intl.formatMessage(messages[`prospectus.course.nav.${key}`])}
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
              </div>
              <nav className={classNames('nav d-none', { 'd-lg-block': !isSpanish }, { 'd-xl-block': isSpanish })}>
                <ul className="list-unstyled mb-0 d-flex flex-wrap py-3 m-1">
                  {getValidNavLinks().map((key) => (
                    <li key={`nav-${key}`}>
                      <button
                        className={`${getListStyling(`nav-${key}`)} nav-button ${isSpanish && 'spanish'}`}
                        type="button"
                        id={`nav-${key}`}
                        onClick={handleClick}
                      >
                        {intl.formatMessage(messages[`prospectus.course.nav.${key}`])}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className={`ml-4 pl-2 ${!isNavStuck && !webview ? 'd-sm-none' : ''}`}>
                <EnrollButtonLink
                  emailOptIn={emailOptIn}
                  buttonClass="nav-enroll py-2"
                  hideAsSecondaryButton
                  trackingID="course-nav"
                  uuid={uuid}
                  buttonLabel={intl.formatMessage(messages['prospectus.course.nav.enroll'])}
                  owners={owners}
                  cohesionVariant={courseType}
                  cohesionCategory={subjects.length > 0 ? subjects[0].name : 'not-available'}
                  cohesionName={title}
                >
                  <div className="nav-enroll-inner py-1">
                    <h4 className="text-white m-0">{intl.formatMessage(messages['prospectus.course.nav.enroll'])}</h4>

                    <div className="x-small"> {getCourseRunTilePrimaryText(defaultCourseRun, intl)}</div>
                  </div>
                </EnrollButtonLink>
              </div>
            </Container>
          </div>
        </section>
      )}
    </WebviewContext.Consumer>
  );
}

BaseStickyNav.propTypes = {
  faq: PropTypes.string,
  fullDescription: PropTypes.string,
  outcome: PropTypes.string,
  syllabusRaw: PropTypes.string,
  intl: intlShape.isRequired,
  title: PropTypes.string.isRequired,
  scrollToRef: PropTypes.func.isRequired,
  semanticRefMap: PropTypes.objectOf(
    PropTypes.shape({
      current: PropTypes.shape(),
    }),
  ).isRequired,
  showCourseModes: PropTypes.bool,
  uuid: PropTypes.string.isRequired,
  emailOptIn: PropTypes.bool,
  defaultCourseRun: PropTypes.shape(courseRunShape).isRequired,
  owners: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  courseType: PropTypes.string,
  subjects: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
};

BaseStickyNav.defaultProps = {
  faq: '',
  fullDescription: null,
  outcome: null,
  syllabusRaw: null,
  showCourseModes: false,
  emailOptIn: false,
  courseType: null,
  subjects: [],
};

const mapStateToProps = (store) => ({
  defaultCourseRun: store.activeCourseRun,
});

export default connect(mapStateToProps)(injectIntl(BaseStickyNav));
